import EventBus from '@core/eventbus';
import { loadFromAPIs } from '@utilities/fetch';
import { parseHtml } from '@utilities/parsing';
import D3Throttle from '@utilities/throttle';
import { uefaBaseUrl } from '@utilities/WindowContext';

export type LazyLoadPluginOptions = {
  libraryUrl?: string;
  startPage?: number;
  scrollThreshold?: number;
  append?: boolean;
  handlersDelay?: number;
  targetSelector?: string;
  contentSelector?: string;
  emptyMessageSelector?: string;
};

const defaults: LazyLoadPluginOptions = {
  libraryUrl: '',
  startPage: 1,
  scrollThreshold: 150,
  append: true,
  handlersDelay: 250,
  targetSelector: '',
  contentSelector: '',
  emptyMessageSelector: '',
};

export default class LazyLoadPlugin {
  opt: LazyLoadPluginOptions;
  dom: HTMLElement;
  target: HTMLElement;
  page: number;
  throttle: D3Throttle<() => void>;
  busy: boolean;

  constructor(dom: HTMLElement, options: LazyLoadPluginOptions) {
    this.opt = {
      ...defaults,
      ...options,
    };

    this.dom = dom;

    this.target = this.opt.targetSelector ? this.dom.querySelector(this.opt.targetSelector) : this.dom;

    this.page = this.opt.startPage;
  }

  init() {
    const events = ['scroll.lazyload', 'resize.lazyload'];
    this.throttle = new D3Throttle(window, this.checkScroll, this.opt.handlersDelay, events, this);

    this.checkScroll();
  }

  detach() {
    this.throttle?.off();
  }

  checkScroll() {
    if (!this.target) return;

    const wb = window.scrollY + window.innerHeight,
      eb = this.target.clientHeight,
      oop = eb - this.opt.scrollThreshold > wb;

    if (oop) return;

    this.load(this.page + 1);
  }

  async load(newPage: number) {
    if (newPage === this.page || this.busy) return;

    this.busy = true;

    this.dom.classList.add('loading');

    let url: string;
    if (this.opt.libraryUrl.includes('{page}')) {
      url = this.opt.libraryUrl.replace('{page}', `${newPage}`);
    } else {
      url = `${this.opt.libraryUrl}${newPage}`;
    }

    const response = await loadFromAPIs(url, {}, { responseType: 'text', apiUrl: uefaBaseUrl });

    let content = parseHtml(response);
    if (content && this.opt.contentSelector)
      content = content.matches(this.opt.contentSelector)
        ? content.parentNode.querySelector(this.opt.contentSelector)
        : content;

    if (content && !(this.opt.emptyMessageSelector && content.matches(this.opt.emptyMessageSelector))) {
      if (this.opt.append) {
        content.childNodes.forEach((node) => {
          const nodeContent = parseHtml((node as HTMLElement).outerHTML);
          if (nodeContent) this.target.append(nodeContent);
        });
      } else this.target.innerHTML = content.innerHTML;
    } else {
      this.throttle.off();
    }

    EventBus.dispatch('lazyload-loaded', this.dom);
    this.dom.classList.remove('loading');

    const prevValue = {};
    this.dom.querySelectorAll('.js__show-only-first').forEach((el: HTMLElement) => {
      const key = el.dataset.key ?? 'default';
      const value = el.dataset.value;
      el.classList.toggle('hidden', prevValue[key] === value);
      prevValue[key] = value;
    });

    this.page = newPage;
    this.busy = false;
  }
}
